import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { auth } from '@/lib/api';
import { getStoredUser, setStoredUser } from '@/lib/auth';
import { 
  Clock, Loader2, ArrowLeft, User, Phone, BookOpen, Calendar, 
  Camera, Save, Mail, Lock, Eye, EyeOff, Trash2, AlertTriangle, Bell, Volume2
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { fetchNotificationPrefs, getCachedNotificationPrefs, saveNotificationPrefs } from '@/lib/notificationPrefs';
import { previewPositionFilledSound } from '@/lib/audioSettings';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import BrandLogo from '@/components/BrandLogo';
import AppFooter from '@/components/AppFooter';

export default function ProfilePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(getStoredUser());
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    subjects: user?.subjects || '',
    availability: user?.availability || ''
  });
  // KAN-156: school-admin notification preferences (saved to the account)
  const [positionFilledSound, setPositionFilledSound] = useState(getCachedNotificationPrefs().position_filled_sound);
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);
  const [isTestingSound, setIsTestingSound] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Refresh user data on mount
    const fetchUser = async () => {
      try {
        const res = await auth.me();
        setUser(res.data);
        setStoredUser(res.data);
        setFormData({
          name: res.data.name || '',
          phone: res.data.phone || '',
          subjects: res.data.subjects || '',
          availability: res.data.availability || ''
        });
      } catch (error) {
        // Silent fail - user data will use stored values
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (user?.role !== 'employer') return;
    fetchNotificationPrefs()
      .then((p) => setPositionFilledSound(p.position_filled_sound))
      .catch(() => { /* keep cached value */ });
  }, [user?.role]);

  const handleTogglePositionFilledSound = async (checked) => {
    const previous = positionFilledSound;
    setPositionFilledSound(checked); // optimistic
    setIsSavingPrefs(true);
    try {
      const saved = await saveNotificationPrefs({ position_filled_sound: checked });
      setPositionFilledSound(saved.position_filled_sound);
      toast.success(checked ? 'Position-filled sound turned on' : 'Position-filled sound turned off');
    } catch (_e) {
      setPositionFilledSound(previous);
      toast.error('Could not save your notification setting. Please try again.');
    } finally {
      setIsSavingPrefs(false);
    }
  };

  const handleTestPositionFilledSound = async () => {
    setIsTestingSound(true);
    try {
      const played = await previewPositionFilledSound();
      if (!played) {
        toast.error('Your browser blocked the sound', {
          description: 'Check that this site is allowed to play audio and your device is not muted.',
        });
      }
    } finally {
      setIsTestingSound(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: digitsOnly }));
      return;
    }
    if ((name === 'subjects' || name === 'availability') && value.length > 100) {
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    if (phone.length <= 3) return phone;
    if (phone.length <= 6) return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.phone && formData.phone.length !== 10) {
      toast.error('Phone number must be 10 digits');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const res = await auth.updateProfile(formData);
      setUser(res.data);
      setStoredUser(res.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setIsUploading(true);
    
    try {
      const res = await auth.uploadPhoto(file);
      const updatedUser = { ...user, picture: res.data.picture };
      setUser(updatedUser);
      setStoredUser(updatedUser);
      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setIsUploading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      await auth.changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const goBack = () => {
    navigate(user?.role === 'employer' ? '/employer' : '/toc');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={goBack}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo variant="black" size="lg" />
          </Link>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center gap-6 px-4 sm:px-6 py-12" data-testid="profile-main">
        <Card className="w-full max-w-lg border-slate-200 rounded-xl shadow-sm">
          <CardHeader className="text-left">
            <CardTitle className="text-2xl font-bold font-['Manrope'] text-slate-900">
              My Profile
            </CardTitle>
            <CardDescription className="text-slate-600">
              Update your profile information
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Profile Photo */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative">
                <Avatar className="h-24 w-24 border-4 border-slate-200">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-emerald-100 text-emerald-700 text-2xl">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        size="icon"
                        variant="secondary"
                        className="absolute bottom-0 right-0 h-8 w-8 rounded-full shadow-md cursor-not-allowed opacity-60"
                        disabled
                        data-testid="upload-photo-btn"
                      >
                        <Camera className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Photo upload is coming soon
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                  data-testid="photo-input"
                />
              </div>
              <p className="text-sm text-slate-500 mt-2">Photo upload is coming soon</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email (read-only) */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="rounded-md bg-slate-100"
                />
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Full Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  data-testid="profile-name-input"
                  className="rounded-md"
                />
              </div>
              
              {/* Sub-specific fields */}
              {user?.role === 'toc' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formatPhone(formData.phone)}
                      onChange={handleChange}
                      disabled={isLoading}
                      data-testid="profile-phone-input"
                      className="rounded-md"
                    />
                    <p className="text-xs text-slate-500">10 digits - visible to connected schools</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="subjects" className="text-slate-700 flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      Subjects/Specializations
                    </Label>
                    <Textarea
                      id="subjects"
                      name="subjects"
                      placeholder="e.g., Math K-5, Special Education, ESL"
                      value={formData.subjects}
                      onChange={handleChange}
                      disabled={isLoading}
                      data-testid="profile-subjects-input"
                      className="rounded-md resize-none h-20"
                      maxLength={100}
                    />
                    <p className="text-xs text-slate-500">{formData.subjects.length}/100 characters</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="availability" className="text-slate-700 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Availability
                    </Label>
                    <Textarea
                      id="availability"
                      name="availability"
                      placeholder="e.g., Mon-Fri 8am-3pm, Available on short notice"
                      value={formData.availability}
                      onChange={handleChange}
                      disabled={isLoading}
                      data-testid="profile-availability-input"
                      className="rounded-md resize-none h-20"
                      maxLength={100}
                    />
                    <p className="text-xs text-slate-500">{formData.availability.length}/100 characters</p>
                  </div>
                </>
              )}
              
              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-md"
                disabled={isLoading}
                data-testid="save-profile-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Profile
                  </>
                )}
              </Button>
            </form>
            
            {/* Password Change Section */}
            <Separator className="my-6" />
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Change Password
              </h3>
              
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword" className="text-slate-700">
                    Current Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      name="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="Enter current password"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      required
                      disabled={isChangingPassword}
                      data-testid="current-password-input"
                      className="rounded-md pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-400" />
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-slate-700">
                    New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      name="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      required
                      minLength={6}
                      disabled={isChangingPassword}
                      data-testid="new-password-input"
                      className="rounded-md pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4 text-slate-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-slate-400" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">At least 6 characters</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700">
                    Confirm New Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    disabled={isChangingPassword}
                    data-testid="confirm-password-input"
                    className="rounded-md"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  variant="outline"
                  className="w-full rounded-md"
                  disabled={isChangingPassword}
                  data-testid="change-password-btn"
                >
                  {isChangingPassword ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Changing Password...
                    </>
                  ) : (
                    <>
                      <Lock className="mr-2 h-4 w-4" />
                      Change Password
                    </>
                  )}
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
        
        {/* Notifications Section (school admins) — KAN-156 */}
        {user?.role === 'employer' && (
          <Card className="w-full max-w-lg border-slate-200 rounded-xl shadow-sm" data-testid="notification-settings-section">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2 text-slate-900">
                <Bell className="h-5 w-5" />
                Notifications
              </CardTitle>
              <CardDescription>
                Choose how ExpressSub alerts you while you're signed in.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <Switch
                    id="position-filled-sound"
                    checked={positionFilledSound}
                    onCheckedChange={handleTogglePositionFilledSound}
                    disabled={isSavingPrefs}
                    className="mt-0.5 data-[state=checked]:bg-emerald-600"
                    data-testid="position-filled-sound-toggle"
                  />
                  <div className="min-w-0">
                    <Label htmlFor="position-filled-sound" className="text-sm font-medium text-slate-900 cursor-pointer">
                      Play sound when a position is filled
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      A soft chime plays as soon as a Sub accepts one of your positions.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="self-start sm:self-center shrink-0"
                  onClick={handleTestPositionFilledSound}
                  disabled={isTestingSound}
                  data-testid="test-position-filled-sound-btn"
                >
                  {isTestingSound ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Volume2 className="h-4 w-4 mr-1.5" />}
                  Test Sound
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delete Account Section */}
        {user?.role !== 'subscription_manager' && (
          <Card className="w-full max-w-lg border-red-200 bg-red-50/30 rounded-xl" data-testid="delete-account-section">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2 text-red-700">
                <Trash2 className="h-5 w-5" />
                Delete Account
              </CardTitle>
              <CardDescription className="text-red-600/70">
                Permanently delete your account and all associated data. This action cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="text-red-700 border-red-300 hover:bg-red-100"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-account-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete My Account
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                Delete Your Account?
              </DialogTitle>
              <DialogDescription>
                This will permanently delete your profile, messages, notifications, and all associated data. If you are a substitute, your accepted positions will be reopened. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <Label className="text-sm">Type <strong>DELETE</strong> to confirm:</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                data-testid="delete-confirm-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                onClick={async () => {
                  setIsDeleting(true);
                  try {
                    await auth.deleteAccount();
                    localStorage.clear();
                    toast.success('Account deleted');
                    navigate('/');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to delete account');
                  } finally {
                    setIsDeleting(false);
                  }
                }}
                data-testid="confirm-delete-account-btn"
              >
                {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Permanently Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
      <AppFooter role={user?.role} />
    </div>
  );
}
